import { TFunction } from 'i18next';
import { FeatureLifecycleStatus } from '@/models/common/FeatureLifecycleStatus';

/*
 * The server writes enums as numbers in its audit snapshots (System.Text.Json's default) and as
 * names in its DTOs. These read either.
 */
const STATUS_BY_NUMBER = [FeatureLifecycleStatus.NotReleased, FeatureLifecycleStatus.InRollout, FeatureLifecycleStatus.FullyReleased];
const CLASSIFICATION_BY_NUMBER: Record<number, string> = {
  1: 'Welfare',
  2: 'Community',
  3: 'Kollel',
  4: 'CampaignForWelfare',
  5: 'CampaignForCommunity',
  6: 'DigitalWallet',
  7: 'WelfareDonex',
};
const ROLE_BY_NUMBER = ['Admin', 'User', 'Debugger'];

export const statusName = (value: unknown): FeatureLifecycleStatus | null => {
  if (typeof value === 'number') return STATUS_BY_NUMBER[value] ?? null;
  if (typeof value === 'string' && (STATUS_BY_NUMBER as string[]).includes(value)) return value as FeatureLifecycleStatus;
  return null;
};

export const classificationName = (value: unknown): string => (typeof value === 'number' ? CLASSIFICATION_BY_NUMBER[value] ?? String(value) : String(value));
export const roleName = (value: unknown): string => (typeof value === 'number' ? ROLE_BY_NUMBER[value] ?? String(value) : String(value));

export interface NameMaps {
  fundName: (id: number) => string;
  userName: (email: string) => string;
  groupName: (id: number) => string;
}

/** «קופה ראשית, אברך מאמשינוב ועוד 3» - a few names and how many more, for a hover or a history line. */
export const nameList = (names: string[], t: TFunction, max = 4): string => {
  if (names.length <= max) return names.join(', ');
  return t('featureFlags.namesMore', { names: names.slice(0, max).join(', '), count: names.length - max });
};

export interface TargetingSnapshot {
  fundIds: number[];
  groupIds: number[];
  userEmails: string[];
  classifications: unknown[];
  roles: unknown[];
}

/** One line per kind that has anything in it: «פרויקטים: …», «משתמשים: …». */
export const targetingLines = (snapshot: TargetingSnapshot, maps: NameMaps, t: TFunction): string[] => {
  const lines: string[] = [];
  const add = (kind: string, names: string[]) => {
    if (names.length > 0) lines.push(`${t(`featureFlags.dialog.picker.kinds.${kind}`)}: ${nameList(names, t)}`);
  };
  add('funds', snapshot.fundIds.map(maps.fundName));
  add('groups', snapshot.groupIds.map(maps.groupName));
  add('classifications', snapshot.classifications.map((c) => t(`featureFlags.classificationLabels.${classificationName(c)}`, classificationName(c))));
  add('users', snapshot.userEmails.map(maps.userName));
  add('roles', snapshot.roles.map((r) => t(`featureFlags.roleLabels.${roleName(r)}`, roleName(r))));
  return lines;
};
